import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface AssistantWhatsappLink {
  id: string;
  phoneNumber: string;
  verifiedAt: string;
}

export interface AssistantThreadSummary {
  id: number;
  title: string | null;
  updated_at: string;
}

export interface AssistantMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface AssistantThread {
  id: number;
  title: string | null;
  messages: AssistantMessage[];
}

/**
 * P.A. dashboard assistant client (T199, I.pa): the six /api/v1/assistant
 * routes. The tenant is the JWT on the interceptor, never a request field
 * (V178). Stateless on purpose — a cached thread list would outlive a logout
 * and show one tenant's chats to the next.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly threadsUrl = `${environment.baseUrl}/api/v1/assistant/threads`;
  private readonly whatsappUrl = `${environment.baseUrl}/api/v1/assistant-whatsapp/link`;

  constructor(private http: HttpClient) {}

  whatsappLink(): Observable<{ links: AssistantWhatsappLink[] }> {
    return this.http.get<{ links: AssistantWhatsappLink[] }>(this.whatsappUrl);
  }

  createWhatsappLink(): Observable<{ id: string; code: string; expiresAt: string; whatsappNumber: string | null }> {
    return this.http.post<{ id: string; code: string; expiresAt: string; whatsappNumber: string | null }>(this.whatsappUrl, {});
  }

  removeWhatsappLink(id: string): Observable<void> {
    return this.http.delete<void>(`${this.whatsappUrl}/${encodeURIComponent(id)}`);
  }

  /** T203: is the P.A. switched on for this deployment (backend env)? */
  status(): Observable<{ enabled: boolean }> {
    return this.http.get<{ enabled: boolean }>(`${environment.baseUrl}/api/v1/assistant/status`);
  }

  listThreads(): Observable<AssistantThreadSummary[]> {
    return this.http.get<AssistantThreadSummary[]>(this.threadsUrl);
  }

  createThread(): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(this.threadsUrl, {});
  }

  getThread(id: number): Observable<AssistantThread> {
    return this.http.get<AssistantThread>(`${this.threadsUrl}/${id}`);
  }

  renameThread(id: number, title: string): Observable<AssistantThreadSummary> {
    return this.http.patch<AssistantThreadSummary>(`${this.threadsUrl}/${id}`, { title });
  }

  deleteThread(id: number): Observable<void> {
    return this.http.delete<void>(`${this.threadsUrl}/${id}`);
  }

  sendMessage(
    id: number,
    content: string
  ): Observable<{ userMessage: AssistantMessage; assistantMessage: AssistantMessage }> {
    return this.http.post<{ userMessage: AssistantMessage; assistantMessage: AssistantMessage }>(
      `${this.threadsUrl}/${id}/messages`,
      { content }
    );
  }
}
